import React from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Sparkle, CheckCircle } from "@phosphor-icons/react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const DIMS = [
  ["Brand", "brand_accuracy"], ["Logo", "logo_placement"], ["Market", "marketing_effectiveness"],
  ["Appeal", "visual_appeal"], ["Convert", "conversion_potential"], ["Read", "readability"], ["CTA", "cta_visibility"],
];

export function ImproveCompareDialog({ data, onClose, onKeepImproved }) {
  const o = data?.original?.score || {};
  const n = data?.improved?.score || {};
  const delta = (n.overall ?? 0) - (o.overall ?? 0);
  return (
    <Dialog open={!!data} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="bg-[#14141F] border-white/10 text-white max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="improve-compare-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkle size={18} className="brand-text" weight="fill" /> Creative Director's Second Pass — {data?.concept}
          </DialogTitle>
          <DialogDescription className="sr-only">Side-by-side comparison of the original and improved designs with AI scores.</DialogDescription>
        </DialogHeader>
        {data && (() => {
          const worse = data.is_improvement === false || delta < 0;
          return (
          <div className="space-y-4">
            {worse && (
              <div data-testid="improve-worse-banner" className="rounded-lg bg-red-500/[0.08] border border-red-500/25 px-3 py-2 text-xs text-red-300 font-semibold">
                This regeneration scored LOWER than the original ({n.overall} vs {o.overall}). We recommend keeping the original — do not treat this as an improvement.
              </div>
            )}
            <div className="flex items-center justify-center gap-4" data-testid="improve-score-summary">
              <div className="text-center">
                <div className="text-[10px] label-caps text-[#7a7a7a]">Original Score</div>
                <div className="text-3xl font-head font-bold text-[#9a9a9a]">{o.overall ?? "—"}</div>
              </div>
              <span className="text-2xl text-[#7a7a7a]">→</span>
              <div className="text-center">
                <div className="text-[10px] label-caps text-[#7a7a7a]">{worse ? "Second Pass Score" : "Improved Score"}</div>
                <div className={`text-3xl font-head font-bold ${delta > 0 ? "text-emerald-400" : delta < 0 ? "text-red-400" : "text-amber-400"}`}>{n.overall ?? "—"}</div>
              </div>
              {delta !== 0 && (
                <span className={`text-sm font-semibold px-2 py-0.5 rounded-full ${delta > 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`} data-testid="improve-delta">
                  {delta > 0 ? `+${delta}` : delta}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] label-caps text-[#7a7a7a] mb-1">Original</div>
                <img src={`${BACKEND}${data.original?.image_path}`} alt="original" className={`w-full rounded-lg border ${worse ? "border-emerald-500/40 border-2" : "border-white/10 opacity-80"}`} data-testid="improve-original-img" />
              </div>
              <div>
                <div className={`text-[10px] label-caps mb-1 ${worse ? "text-red-400" : "text-emerald-400"}`}>{worse ? "Second Pass (scored lower)" : "Improved"}</div>
                <img src={`${BACKEND}${data.improved?.image_path}`} alt="improved" className={`w-full rounded-lg ${worse ? "border border-red-500/30 opacity-80" : "border-2 border-emerald-500/40"}`} data-testid="improve-improved-img" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-lg bg-white/[0.03] border border-white/8 p-3" data-testid="improve-dim-deltas">
                <div className="text-[10px] label-caps text-[#7a7a7a] mb-2">What changed</div>
                {DIMS.map(([lbl, key]) => {
                  const ov = o[key], nv = n[key];
                  if (ov == null || nv == null) return null;
                  const d = nv - ov;
                  return (
                    <div key={key} className="flex items-center justify-between text-[11px] py-0.5">
                      <span className="text-[#9a9a9a]">{lbl}</span>
                      <span className="flex items-center gap-1.5">
                        <span className="text-[#7a7a7a]">{ov}</span><span className="text-[#555]">→</span>
                        <span className={d > 0 ? "text-emerald-400 font-semibold" : d < 0 ? "text-red-400" : "text-[#cfcfcf]"}>{nv}</span>
                        {d !== 0 && <span className={`text-[9px] ${d > 0 ? "text-emerald-400" : "text-red-400"}`}>{d > 0 ? `+${d}` : d}</span>}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="rounded-lg bg-white/[0.03] border border-white/8 p-3 space-y-2">
                {(data.focus || []).length > 0 && (
                  <div data-testid="improve-focus">
                    <div className="text-[10px] label-caps text-[#7a7a7a] mb-1">Director focused on</div>
                    <div className="flex flex-wrap gap-1">
                      {data.focus.map((f) => <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-[#6B3DFF]/15 text-[#C39BFF] border border-[#6B3DFF]/25">{f}</span>)}
                    </div>
                  </div>
                )}
                {(data.preserved || []).length > 0 && (
                  <div>
                    <div className="text-[10px] label-caps text-[#7a7a7a] mb-1">Preserved (already strong)</div>
                    <div className="flex flex-wrap gap-1">
                      {data.preserved.map((f) => <span key={f} className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-400 border border-emerald-500/25">{f}</span>)}
                    </div>
                  </div>
                )}
                {(n.reasons || []).slice(0, 3).map((r, i) => <div key={i} className="text-[10px] text-[#9a9a9a] leading-snug">• {r}</div>)}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button data-testid="improve-keep-original" variant={worse ? "default" : "outline"} onClick={onClose} className={worse ? "bg-emerald-600 hover:bg-emerald-500 text-white" : "border-white/15 bg-transparent text-white hover:bg-white/5"}>{worse ? "Keep Original (recommended)" : "Keep Original"}</Button>
              <Button data-testid="improve-keep-improved" onClick={onKeepImproved} variant={worse ? "outline" : "default"} className={worse ? "border-white/15 bg-transparent text-white hover:bg-white/5" : "bg-emerald-600 hover:bg-emerald-500 text-white"}><CheckCircle size={15} className="mr-1.5" weight="fill" /> {worse ? "Use Second Pass anyway" : "Keep Improved"}</Button>
            </div>
          </div>
          );
        })()}
      </DialogContent>
    </Dialog>
  );
}
